import { makeApiRequest } from "$lib/utils/api";

/**
 * The org's saved **default info types** for the Exposed Sensitive Documents page (#2199 route).
 *
 * Path note: the management router mounts this under `/orgs/{org}/exposed-sensitive-documents-settings`
 * and both handlers are registered on `"/"`, so the **trailing slash is required** - without it
 * FastAPI answers a redirect, not the document.
 *
 * Wire shape is camelCase (`CamelCaseBaseModel` serialises by alias):
 * `{ defaultInfoTypes: string[], modified: string | null, modifiedBy: string | null }`.
 */
const settingsPath = (orgId: string) => `/orgs/${orgId}/exposed-sensitive-documents-settings/`;

/**
 * The org default, or `null` when there is none to honour.
 *
 * `null` is the page's "show all info types" - and it is deliberately also what a failed or
 * forbidden `GET` produces. The spec (R4) wants a reader without `org:read` on the settings, or a
 * reader whose request simply fails, to see the whole page rather than an error: there is nothing
 * they could do about it and nothing is being hidden from them. `makeApiRequest` throws on any
 * non-OK response (a 403 arrives as `Error("Forbidden")`), so the swallow has to be a `catch`, not
 * a status check. An org that never saved reads back `defaultInfoTypes: []`, which
 * `resolveActiveInfoTypes` already treats as show-all, so it is passed through unchanged.
 */
export async function fetchDefaultInfoTypes(orgId: string): Promise<string[] | null> {
  try {
    const response = await makeApiRequest(settingsPath(orgId), {}, "GET");
    const defaultInfoTypes = (response as { defaultInfoTypes?: unknown } | undefined)?.defaultInfoTypes;
    if (!Array.isArray(defaultInfoTypes)) return null;
    return defaultInfoTypes.filter((infoType): infoType is string => typeof infoType === "string");
  } catch {
    return null;
  }
}

/**
 * Save the org default (requires `org:write`; #2208 is the caller) and return **what was stored**.
 *
 * The route de-duplicates and orders the list before writing, then echoes the stored document
 * back (spec R4), so the response - not the request body - is what the rest of the org will read
 * on its next `GET`. Callers adopt the return value; adopting the body they sent would show the
 * saver a list the server has already rewritten. A response missing the key falls back to the
 * request, which is the only other list anyone could honestly claim was saved.
 *
 * Errors are **not** swallowed here, unlike the read: a save that silently did nothing would be
 * the worst of both worlds. An empty list survives as an empty list, i.e. "the org default is
 * show all".
 */
export async function saveDefaultInfoTypes(orgId: string, infoTypes: string[]): Promise<string[]> {
  const response = await makeApiRequest(settingsPath(orgId), { defaultInfoTypes: infoTypes }, "PUT");
  const saved = (response as { defaultInfoTypes?: unknown } | undefined)?.defaultInfoTypes;
  if (!Array.isArray(saved)) return infoTypes;
  return saved.filter((infoType): infoType is string => typeof infoType === "string");
}
